import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import CustomAutoComplete from "components/shared/autoCompletes/CustomAutoComplete";
import CustomContentDialog from "components/shared/dialog/CustomContentDialog";
import { FormatMoneyTypography, FormatPercentageTypography } from "components/shared/format/Formats";
import useDialog from "hooks/dialog/useDialog";
import { useAutocomplete } from "hooks/forms/FormHooks";
import { useLoadingToast } from "hooks/toast/toasts";
import { useState } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { createChargeAndMarkAsPaid } from "services/api/api";
import { useFinancesProjects } from "../../../hooks/api/financesHooks";
import { formatDate } from "../../../services/date";
import { changeFinancePage } from "../../../store/ducks/finances-page-reducer";

export default function FinancesProjects({ filters }) {
  const page = useSelector((state) => state.financesPage.value);
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [billingOptions, setBillingOptions] = useState([]);
  const queryClient = useQueryClient();

  const { initializeLoadingToast, updateLoadingToast } = useLoadingToast();

  const [isSendBillLoading, setIsSendBillLoading] = useState(false);
  const {
    open: billDialogOpen,
    handleClickOpen: handleBillDialogOpen,
    handleClickClose: handleBillDialogClose,
  } = useDialog();

  const { reset: resetBillingKind, ...billingKind } = useAutocomplete();

  const defaultBillingKinds = [{ name: "ENTRADA" }, { name: "VISTORIA" }, { name: "INTEGRAL" }];

  const [rowsPerPage, setRowsPerPage] = useState(20);
  const paymentsQuery = useFinancesProjects(filters, { page, size: rowsPerPage });

  const calculateBillingOptions = (project) => {
    let options = defaultBillingKinds;
    const paymentsArray = JSON.parse(project.paymentsArray) || [];
    const isPaid = (p) => p.status === "PAID" || p.status === "MARKED_AS_PAID";
    paymentsArray.forEach((p) => {
      if (p.paymentKindId === 3 && isPaid(p)) {
        options = [];
      } else if (p.paymentKindId === 2 && isPaid(p)) {
        options = defaultBillingKinds.filter((item) => item.name === "ENTRADA");
      } else if (p.paymentKindId === 1 && isPaid(p)) {
        options = defaultBillingKinds.filter((item) => item.name === "VISTORIA");
      }
    });
    return options;
  };

  const handleChangePage = (event, newPage) => {
    dispatch(changeFinancePage(newPage));
  };

  if (paymentsQuery.isLoading) {
    return <CircularProgress />;
  }

  if (paymentsQuery.isError) {
    return <div>Error loading payments...</div>;
  }

  const payments = paymentsQuery.data?.items || [];
  const totalItems = paymentsQuery.data?.pagination.totalItems;

  const handleClick = (event, project) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openBillingDialog = () => {
    if (selectedProject) {
      const options = calculateBillingOptions(selectedProject);
      setBillingOptions(options);
      handleBillDialogOpen();
    }
    handleMenuClose();
  };

  const dialogContent = (
    <CustomAutoComplete
      params={billingKind}
      options={billingOptions}
      disabled={false}
      label="Tipo de Pagamento"
      width="100%"
    />
  );

  const handleMarkAsPaid = async () => {
    if (!selectedProject) return;

    let paidToastId;
    setIsSendBillLoading(true);

    try {
      paidToastId = initializeLoadingToast({
        text: `Atualizando pagamento...`,
      });

      const projectId = selectedProject.projectId;
      const { status } = await createChargeAndMarkAsPaid(projectId, billingKind.value.name);

      if (status === 200) {
        queryClient.invalidateQueries({ queryKey: ["financesProjects"] });
        // queryClient.invalidateQueries({ queryKey: ["summary"] });

        updateLoadingToast({
          id: paidToastId,
          text: `Pagamento Atualizado com sucesso`,
          type: "success",
        });
        handleBillDialogClose();
      }
    } catch (error) {
      updateLoadingToast({
        id: paidToastId,
        text:
          error.response.data.message ??
          `Erro ao atualizar pagamento, 
        tente novamente`,
        type: "error",
      });
    } finally {
      setIsSendBillLoading(false);
    }
  };

  return (
    <Paper sx={{ mt: 5 }}>
      <CustomContentDialog
        open={billDialogOpen}
        handleClickClose={handleBillDialogClose}
        title="Marcar valor como pago"
        content={dialogContent}
        requestLoading={isSendBillLoading}
        handleConfirmationClick={handleMarkAsPaid}
        confirmationClickText="Confirmar"
        requestLoadingText="Confirmando..."
        width={500}
        height={100}
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {[
                { id: "projectNumber", label: "Projeto" },
                { id: "projectName", label: "Nome" },
                { id: "createTime", label: "Criação" },
                { id: "clientName", label: "Integrador" },
                { id: "stageName", label: "Etapa" },
                { id: "projectPrice", label: "Valor Projeto" },
                { id: "paidValue", label: "Valor Pago" },
                { id: "actions", label: "Ações" },
              ].map((headCell, i) => (
                <TableCell key={i}>{headCell.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((item) => {
              const { paidValue, projectPrice } = item;
              const paidPercentage = projectPrice
                ? paidValue
                  ? (paidValue / projectPrice) * 100
                  : 0
                : null;

              const color =
                paidPercentage !== null
                  ? paidPercentage === 100
                    ? "#00a04e"
                    : paidPercentage >= 50
                    ? "#cd9100"
                    : "#ff474c"
                  : "#abacb0";

              return (
                <TableRow key={item.projectId}>
                  <TableCell>{item.projectNumber}</TableCell>
                  <TableCell>{item.projectName}</TableCell>
                  <TableCell>{item.createTime ? formatDate(item.createTime) : ""}</TableCell>
                  <TableCell>{item.clientName}</TableCell>
                  <TableCell>{item.stageName}</TableCell>
                  <TableCell>
                    <FormatMoneyTypography value={item.projectPrice ?? 0} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 100, textAlign: "center" }}>
                    {projectPrice && (
                      <Box
                        sx={{
                          textAlign: "center",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <FormatMoneyTypography
                          value={item.paidValue ?? 0}
                          sx={{ fontWeight: "bold" }}
                        />
                        <FormatPercentageTypography value={paidPercentage} sx={{ color }} />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.projectPrice !== 0 && item.paidValue < item.projectPrice && (
                      <>
                        <IconButton aria-label="more" onClick={(event) => handleClick(event, item)}>
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          keepMounted
                          open={Boolean(anchorEl) && selectedProject?.projectId === item.projectId}
                          onClose={handleMenuClose}
                        >
                          <MenuItem onClick={openBillingDialog}>Marcar como pago</MenuItem>
                        </Menu>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        labelRowsPerPage="Registros por página"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        count={totalItems}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[5]}
        onRowsPerPageChange={() => {}}
      />
    </Paper>
  );
}
